/**
 * 筛选器配置面板主组件
 * 基于需求文档 requirements.md 和技术方案 design.md
 */

import React, { useEffect, useReducer, useMemo, useRef, useCallback } from 'react';
import { useIntl, useSelector, shallowEqual } from 'umi';
import { Button, Switch, Form, notification } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import { isEmpty } from 'lodash-es';
import { ScopeType } from '@dbl/services/dashboard';
import { AI_ANALYSIS_TYPE } from '@dbl/core/dashboard';
import FilterScope, { FilterScopeChart } from '@/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/EditFilter/components/FilterScope';
import RelevantFilterConfig from '@/pages/datalab/pages/WorkBench/components/RightModal/ExitModal/EditFilter/components/RelevantFilterConfig';
import { filterReducer, initFilterState } from './reducer';
import type {
  FilterAction,
  IColumnStatusRef,
  IDateDefaultValue,
  IEditFilterProps,
  IExistingFilter,
  IFilterConfig,
  IFilterState,
  IScopeChart,
  IViewListItem,
  TRelevantFilterItem,
} from './types';
import FilterBasicInfo from './components/FilterBasicInfo';
import {
  collectValidationErrors,
  buildFilterConfig,
  getDatasetListChartId,
} from './utils';
import './index.scss';

interface IDatalabRootState {
  datalab?: {
    gameCode?: string;
  };
}

const EditFilter: React.FC<IEditFilterProps> = ({
  visible,
  onClose,
  initialFilter,
  viewList,
  existingFilters,
  // datasetList, // 注意：当前未使用，但保留在 props 中以便后续扩展使用
  onConfirm,
  gameCode: gameCodeProp,
  // system = 'datalab', // 注意：当前未使用，但保留在 props 中以便后续支持多系统场景
}) => {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 从 Redux 获取 gameCode（如果 props 中没有传入）
  const gameCodeFromStore = useSelector(
    (state: IDatalabRootState) => state?.datalab?.gameCode,
    shallowEqual
  );
  const gameCode = gameCodeProp || gameCodeFromStore;

  // 使用 useReducer 管理状态
  const [state, dispatch] = useReducer(
    (state: IFilterState, action: FilterAction) =>
      filterReducer(state, action, {
        existingFilters,
        isEn,
      }),
    initFilterState()
  );

  const columnStatusRef = useRef<IColumnStatusRef | null>(state.columnInfo || null);

  // 判断日期筛选器是否可以设置对比时间
  // 规则：只允许一个日期筛选器设置对比时间，如果已有其他筛选器设置了对比时间，则当前筛选器不能设置
  const isDisabledCompare = useMemo(() => {
    return existingFilters.some(
      (item: IExistingFilter) =>
        item.filterType === 'date' &&
        initialFilter?.i &&
        initialFilter.i !== item.i &&
        !isEmpty(item.defaultCompareTimes)
    );
  }, [existingFilters, initialFilter]);

  // 获取 Scope 图表数据
  // 根据选择的数据表，计算哪些图表属于"同数据表"（sameDatasetCharts），哪些属于"特定图表"（specificCharts）
  // 规则：
  // 1. 如果图表使用了当前选择的数据表，且不是自定义字段（或自定义字段来自当前图表），则归为"同数据表"
  // 2. 否则归为"特定图表"，需要用户手动选择并配置关联字段
  // 3. 文本卡片不参与 Scope 计算
  const getScopeCharts = useCallback(
    (viewList: IViewListItem[], dataset: string | null) => {
      const sameDatasetCharts: FilterScopeChart[] = [];
      const specificCharts: FilterScopeChart[] = [];
      const scopeCharts: IScopeChart[] = [];

      viewList?.forEach?.((view) => {
        try {
          const {
            chart_dataset_list: chartDatasetList,
            name,
            name_cn: nameCn,
            task_id: taskIdRaw,
            chartType,
            chart_type: chartTypeLegacy,
          } = view || ({} as IViewListItem);

          const resolvedChartType = chartType ?? chartTypeLegacy;

          if (
            resolvedChartType === 'text' ||
            resolvedChartType === AI_ANALYSIS_TYPE
          ) {
            return;
          }

          const chartId = String(taskIdRaw);
          // 如果是自定义字段，需要判断是否来自当前图表
          // 只有来自当前图表的自定义字段才能归为"同数据表"
          const isSameChart = columnStatusRef.current?.sqlExpression
            ? String(columnStatusRef.current?.customColumnChartId) === chartId
            : true;

          if (
            dataset &&
            chartDatasetList?.some((item) => {
              const { table_id: tableIdLegacy, tableId, id } = item ?? {};
              return String(tableIdLegacy ?? tableId ?? id ?? '') === dataset;
            }) &&
            isSameChart
          ) {
            sameDatasetCharts.push({
              name,
              name_cn: nameCn,
              chartType: resolvedChartType,
              id: chartId,
              columnDatasetId: dataset,
            });

            scopeCharts.push({
              taskId: chartId,
              columnDatasetId: dataset,
              name,
              nameCn,
            });
          } else {
            specificCharts.push({
              name,
              name_cn: nameCn,
              chartType: resolvedChartType,
              id: chartId,
              datasetList: chartDatasetList,
              columnDatasetId: dataset || '',
            });
          }
        } catch (e) {
          // 单条视图异常不影响其余视图的 Scope 计算
          console.log('[EditFilter] scopeCharts view error: ', view?.task_id, e);
        }
      });

      return { sameDatasetCharts, specificCharts, scopeCharts };
    },
    []
  );

  // Scope 图表映射
  const scopeChartsMap = useMemo(() => {
    if (!state.dataset) {
      return { sameDatasetCharts: [], specificCharts: [] };
    }
    return getScopeCharts(viewList, state.dataset);
  }, [viewList, state.dataset, getScopeCharts]);

  // 初始化：从已有筛选器数据恢复（编辑模式）
  // 当 initialFilter 存在时，说明是编辑已有筛选器，需要恢复其配置
  useEffect(() => {
    if (initialFilter) {
      // 编辑回填：拆分 otherInfo 里混用的 chartId（可能是逗号列表或旧版单图 id）
      if (initialFilter.otherInfo) {
        const listChartId = getDatasetListChartId(
          viewList,
          initialFilter.dataset,
        );
        columnStatusRef.current = {
          ...initialFilter.otherInfo,
          // chartId 优先用 viewList 聚合的多图 id，保证再次打开时字段列表完整
          chartId:
            listChartId ||
            initialFilter.otherInfo.chartId ||
            '',
          // customColumnChartId：旧数据可能把单图 id 存在 chartId 里（无逗号且无 customColumnChartId 字段）
          customColumnChartId:
            initialFilter.otherInfo.customColumnChartId ||
            (initialFilter.otherInfo.sqlExpression &&
            initialFilter.otherInfo.chartId &&
            !initialFilter.otherInfo.chartId.includes(',')
              ? initialFilter.otherInfo.chartId
              : ''),
        };
      }
      // 从已有筛选器数据恢复状态
      dispatch({ type: 'INIT_FROM_FILTER', payload: initialFilter });
      // 计算 Scope 图表数据
      // 如果已有数据表，需要计算哪些图表可以使用此筛选器
      // 如果 scope 数据不存在（旧数据或新建），则设置默认值：使用"同数据表"模式，包含所有使用该数据表的图表
      if (initialFilter.dataset) {
        const { scopeCharts } = getScopeCharts(viewList, initialFilter.dataset);
        if (!initialFilter.scope && scopeCharts.length > 0) {
          dispatch({
            type: 'SET_SCOPE',
            payload: {
              scopeType: ScopeType.SameDataset,
              scopeCharts,
            },
          });
        }
      }
    }
  }, [initialFilter, viewList, getScopeCharts]);

  // 编辑/回填时保证 ref.chartId 为多图逗号列表；若当前仅为单图 id 或与 viewList 不一致则纠正
  useEffect(() => {
    if (!state.dataset || !viewList?.length) {
      return;
    }
    const computedListChartId = getDatasetListChartId(
      viewList,
      state.dataset,
    );
    if (!computedListChartId) {
      return;
    }
    const currentListChartId = columnStatusRef.current?.chartId || '';
    if (
      !currentListChartId ||
      (!currentListChartId.includes(',') &&
        currentListChartId !== computedListChartId)
    ) {
      columnStatusRef.current = {
        ...columnStatusRef.current,
        chartId: computedListChartId,
      };
    }
  }, [state.dataset, viewList]);

  // 字段变更时更新 Scope
  // 当用户选择了数据表和字段后，自动计算并设置默认的 Scope 配置
  // 规则：如果当前没有 Scope 配置，且存在可用的图表，则自动设置为"同数据表"模式
  const scopeChartsCount = state.scope.scopeCharts.length;
  useEffect(() => {
    if (state.dataset && state.column) {
      const { scopeCharts } = getScopeCharts(viewList, state.dataset);
      // 只有在当前没有 Scope 配置时才自动设置，避免覆盖用户已配置的内容
      // 且仅在 Scope 类型尚未确定时（初始化阶段）自动填充，避免用户手动全部取消后又被强制还原
      if (
        !state.scope.scopeType &&
        scopeChartsCount === 0 &&
        scopeCharts.length > 0
      ) {
        dispatch({
          type: 'SET_SCOPE',
          payload: {
            scopeType: ScopeType.SameDataset,
            scopeCharts,
          },
        });
      }
    }
    /**
     * 依赖只使用 scopeChartsCount（length），是为了表达“只在 scopeCharts 由空变为非空/数量变化时触发自动填充”：
     * - 避免 scopeCharts 内容（引用）变化导致 effect 反复触发，覆盖用户已手动配置的 scope；
     * - 若未来业务需要在“内容变但 length 不变”时也触发，请同步调整此依赖策略。
     */
  }, [state.dataset, state.column, viewList, getScopeCharts, scopeChartsCount]);

  // 同步 columnInfo 到 ref 时保留 chartId（多图列表）与 customColumnChartId，避免 reducer 更新覆盖列表请求参数
  useEffect(() => {
    if (state.columnInfo) {
      const listChartId = columnStatusRef.current?.chartId;
      columnStatusRef.current = {
        ...columnStatusRef.current,
        ...state.columnInfo,
        chartId: listChartId || '',
        customColumnChartId:
          columnStatusRef.current?.customColumnChartId ||
          state.columnInfo.chartId ||
          '',
      };
    }
  }, [state.columnInfo]);

  /**
   * 构建筛选器配置对象
   * 将内部状态转换为外部需要的筛选器配置格式
   */
  const buildFilterConfigFromState = useCallback((): IFilterConfig => {
    // 传入 ref.chartId（多图列表）写入 otherInfo.chartId，与 columnInfo.chartId（单图）区分
    return buildFilterConfig(state, initialFilter?.i, columnStatusRef.current?.chartId);
  }, [state, initialFilter?.i]);

  /**
   * 提交筛选器配置
   * 调用外部回调函数，传递配置对象
   */
  const submitFilter = useCallback(
    (filterConfig: IFilterConfig): void => {
      try {
        onConfirm(filterConfig);
      } catch (error) {
        // 统一兜底错误处理，避免未捕获异常；此处不通过 console.error 上报
        console.log('[EditFilter] submitFilter failed:', error);
        notification.error({
          title: formatMessage({ id: 'dbl_common_save_fail' }),
          duration: 3000,
        });
      }
    },
    [onConfirm, formatMessage]
  );

  /**
   * 确认按钮点击处理
   * 依次执行：校验 -> 构建配置 -> 提交
   */
  const handleConfirm = useCallback((): void => {
    // 1. 校验：收集所有字段级错误并更新到 state.validation，不弹全局校验错误通知
    const errors = collectValidationErrors(state);
    const hasError =
      errors.datasetError ||
      errors.columnError ||
      errors.typeError ||
      errors.nameError ||
      errors.defaultTypeError ||
      errors.defaultValueError ||
      (errors.scopeError && Object.keys(errors.scopeError).length > 0);

    if (hasError) {
      dispatch({
        type: 'SET_VALIDATION',
        payload: errors,
      });
      return;
    }

    // 2. 构建配置
    const filterConfig = buildFilterConfigFromState();

    // 3. 提交
    submitFilter(filterConfig);
  }, [state, buildFilterConfigFromState, submitFilter]);

  /**
   * 级联子筛选器变更回调
   * 处理新增、修改、删除操作
   */
  const handleRelevantFilterChange = useCallback(
    (value: TRelevantFilterItem | null, changeIndex?: number) => {
      const newFilters: TRelevantFilterItem[] = [...state.relevantFilters];
      if (value && changeIndex !== undefined) {
        // 修改
        newFilters[changeIndex] = value;
      } else if (value) {
        // 新增
        newFilters.push(value);
      } else {
        // 删除
        newFilters.splice(changeIndex!, 1);
      }
      dispatch({
        type: 'SET_RELEVANT_FILTERS',
        payload: newFilters,
      });
    },
    [state.relevantFilters, dispatch]
  );

  if (!visible) {
    return null;
  }

  // 判断是否显示级联子筛选器配置
  // 规则：仅当字段为数值类型时隐藏，其余场景（未选字段、字符串字段、日期字段）均显示
  // 原因：级联筛选器用于根据父筛选器的值过滤子筛选器的选项，数值类型不适用；
  //       与旧版本行为对齐——默认始终展示该区域，只有数值字段时不展示
  const showRelevant = !state.columnInfo || state.columnInfo.isNumeric === false;

  return (
    <div className="dbl-edit-filter">
      {/* 顶部：标题区域 */}
      <div className="dbl-edit-filter-header">
        {formatMessage({ id: 'dbl_create_dashboard_filter' })}
      </div>

      {/* 中间：配置内容区域（可滚动） */}
      <div className="dbl-edit-filter-body">
        {/* 基础信息配置 */}
        <div className="dbl-edit-filter-section">
          <FilterBasicInfo
            state={state}
            dispatch={dispatch}
            gameCode={gameCode || ''}
            viewList={viewList}
            existingFilters={existingFilters}
            isEn={isEn}
            isDisabledCompare={isDisabledCompare}
          />
        </div>

        {/* 级联子筛选器配置（条件显示） */}
        {showRelevant && (
          <div className="dbl-edit-filter-section">
            <RelevantFilterConfig
              relevantFilters={state.relevantFilters.map((item) => {
                if (item.filterName) {
                  return item;
                }
                const filter = existingFilters.find((f) => f.i === item.i);
                return {
                  ...item,
                  filterName: filter?.filterName || filter?.title || '',
                };
              })}
              dependFilters={(initialFilter?.dependFilters ?? []).map((item) => ({
                i: item.i,
                filterName: item.filterName || '',
              }))}
              filters={existingFilters}
              dataset={state.dataset || ''}
              onChange={handleRelevantFilterChange}
              currentFilterKey={initialFilter?.i || ''}
            />
          </div>
        )}

        {/* Scope 配置 */}
        <div className="dbl-edit-filter-section">
          <div className="dbl-edit-filter-scope-title">
            {formatMessage({ id: 'dbl_create_dashboard_scope' })}
          </div>
          <Form.Item
            className="dbl-edit-filter-scope-switch"
            label={formatMessage({ id: 'dbl_create_dashboard_scope_show' })}
          >
            <Switch
              value={state.ui.showScope === true}
              onChange={(value: boolean) => {
                dispatch({
                  type: 'SET_UI_STATE',
                  payload: { showScope: value },
                });
              }}
            />
          </Form.Item>
          <FilterScope
            scopeChartsMap={scopeChartsMap}
            dataset={state.dataset || ''}
            value={{
              scopeType: state.scope.scopeType || ScopeType.SameDataset,
              scopeCharts: state.scope.scopeCharts.map(chart => ({
                task_id: chart.taskId,
                columnDatasetId: chart.columnDatasetId,
                name: chart.name,
                nameCn: chart.nameCn,
                apply_to_column: chart.applyToColumn,
                isDefaultDate: chart.isDefaultDate,
              })),
            }}
            onChange={(value: any) => {
              dispatch({
                type: 'SET_SCOPE',
                payload: {
                  scopeType: value.scopeType,
                  scopeCharts: value.scopeCharts.map((chart: any) => ({
                    taskId: chart.task_id,
                    name: chart.name || '',
                    nameCn: chart.nameCn || chart.name_cn || '',
                    columnDatasetId: chart.columnDatasetId,
                    applyToColumn: chart.apply_to_column,
                    isDefaultDate: chart.isDefaultDate,
                  })),
                },
              });
            }}
            validStatus={state.validation.scopeError || {}}
            clearValidStatus={(chartId?: string) => {
              dispatch({
                type: 'SET_VALIDATION',
                payload: {
                  scopeError: chartId
                    ? {
                      ...(state.validation.scopeError || {}),
                      [chartId]: undefined,
                    }
                    : undefined,
                },
              });
            }}
            filterType={state.filterType}
            gameCode={gameCode}
            defaultDateDisabled={
              state.filterType === 'date' &&
              state.defaultValue &&
              (state.defaultValue as IDateDefaultValue).compareTime?.enabled
            }
          />
        </div>
      </div>

      {/* 底部：按钮区域（固定） */}
      <div className="dbl-edit-filter-footer">
        <Button type="primary" onClick={handleConfirm}>
          {formatMessage({ id: 'dbl_common_confirm' })}
        </Button>
        <Button type="weak" onClick={onClose}>
          {formatMessage({ id: 'dbl_common_back' })}
        </Button>
      </div>
    </div>
  );
};

export default EditFilter;
